import type { AssignmentStrategy, Role, WorkspaceRole } from '@/api/types';
import type { BadgeTone } from '@/components/ui';
import { subject, type AppAbility } from '@/app/auth/ability';

export const ROLE_TONE: Record<Role, BadgeTone> = {
  super_admin: 'accent',
  owner: 'accent',
  admin: 'brand',
  supervisor: 'blue',
  agent: 'neutral',
};

/** Roles that exist inside a client workspace, highest first. */
export const WORKSPACE_ROLES: WorkspaceRole[] = ['owner', 'admin', 'supervisor', 'agent'];

export const ROLE_HELP: Record<WorkspaceRole, string> = {
  owner: 'Account owner — everything, including admins and the workspace profile',
  admin: 'Runs the workspace — channels, AI, automation, API keys, supervisors & agents',
  supervisor: 'Team lead — assigns tickets, curates the knowledge base, sees analytics',
  agent: 'Works tickets, live chats and customers',
};

/** Workspace roles the current user may give — straight from the CASL rules the API enforces. */
export function assignableRoles(ability: AppAbility): WorkspaceRole[] {
  return WORKSPACE_ROLES.filter((role) => ability.can('create', subject('User', { role })));
}

/** Whether the current user may edit (or deactivate) someone with `role`. */
export function canManageUser(ability: AppAbility, role: Role): boolean {
  return ability.can('update', subject('User', { role }));
}

/** Comma/space separated skills → normalized slug list. */
export function parseSkills(input: string): string[] {
  return [
    ...new Set(
      input
        .split(/[\s,]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
}

export const STRATEGY_HELP: Record<AssignmentStrategy, string> = {
  round_robin: 'Rotate through available agents in turn',
  least_loaded: 'Agent with the fewest open tickets / chats',
  skills_based: 'Match required skills, then least loaded',
  manual: 'Queue only — agents pick tickets themselves',
};
